import { useEffect } from "react";
import { motion } from "framer-motion";
import { Flame, Droplets } from "lucide-react";

interface SpecRow {
  label: string;
  value: string;
  highlight?: boolean;
}

interface ProductCard {
  model: string;
  specs: SpecRow[];
}

const pyrolysisProducts: ProductCard[] = [
  {
    model: "Batch Design MSBP-23001",
    specs: [
      { label: "Working Process", value: "Open feeding door → Heating for oil → Stop for cooling reactor → Discharge" },
      { label: "Unit Capacity", value: "5~20 TPD", highlight: true },
      { label: "Feedstock Size", value: "≤1600mm" },
      { label: "Feedstock Pretreating", value: "Optional" },
      { label: "Labor Qty", value: "3~6 persons/shift" },
      { label: "Reactor Cooling Time", value: "3~12 hours" },
      { label: "Power", value: "12~25 KW/h" },
      { label: "Maintenance Period", value: "1×/day" },
      { label: "Carbon Discharge", value: "Manual, discharge <80°C or 200°C" },
      { label: "Transportation", value: "1×17.5m FAR (1×40FR + 2×40HC)" },
      { label: "Installation", value: "(2 welders + 1 labor) × 20 days" },
    ],
  },
  {
    model: "Skid-mounted MSKP-24002",
    specs: [
      { label: "Working Process", value: "Open feeding door → Heating for oil → Stop for cooling reactor → Discharge" },
      { label: "Unit Capacity", value: "1~4 TPD", highlight: true },
      { label: "Feedstock Size", value: "≤1600mm" },
      { label: "Feedstock Pretreating", value: "Optional" },
      { label: "Labor Qty", value: "2~6 persons/shift" },
      { label: "Reactor Cooling Time", value: "2~5 hours" },
      { label: "Power", value: "12~25 KW/h" },
      { label: "Maintenance Period", value: "1×/day" },
      { label: "Carbon Discharge", value: "Manual, discharge <80°C or 200°C" },
      { label: "Transportation", value: "1×17.5m FAR (1×40HC)" },
      { label: "Installation", value: "(1 labor) × 1 day" },
    ],
  },
  {
    model: "Semi-Continuous MSCP-24008",
    specs: [
      { label: "Working Process", value: "Auto feeder by sealing → Start heating for oil → Stop for cooling reactor → Discharge" },
      { label: "Unit Capacity", value: "15~25 TPD", highlight: true },
      { label: "Feedstock Size", value: "≤100mm" },
      { label: "Feedstock Pretreating", value: "Required (Shredder/Grinder or Magnetic Separator)" },
      { label: "Labor Qty", value: "1~4 persons/shift" },
      { label: "Reactor Cooling Time", value: "3~5 hours" },
      { label: "Power", value: "15~30 KW/h" },
      { label: "Maintenance Period", value: "7~10×/day" },
      { label: "Carbon Discharge", value: "Automatic, discharge ≥200°C~300°C" },
      { label: "Transportation", value: "2×17.5m FAR + 1×13.5m (1×40FR + 2×40HC)" },
      { label: "Installation", value: "(2 welders + 1 labor) × 20 days" },
    ],
  },
  {
    model: "Continuous MSCP-24006",
    specs: [
      { label: "Working Process", value: "Continuous feeding and discharging, running 24×45~60 days" },
      { label: "Unit Capacity", value: "20~70 TPD", highlight: true },
      { label: "Feedstock Size", value: "≤80mm" },
      { label: "Feedstock Pretreating", value: "Required (Shredder/Grinder and Magnetic Separator)" },
      { label: "Labor Qty", value: "1~4 persons/shift" },
      { label: "Reactor Cooling Time", value: "0 hours (continuous)" },
      { label: "Power", value: "50 KW/h" },
      { label: "Maintenance Period", value: "45~60×/day" },
      { label: "Carbon Discharge", value: "Continuous discharging" },
      { label: "Transportation", value: "2×17.5m FAR + 1×13.5m (2×40FR + 1×40HC)" },
      { label: "Installation", value: "(6 welders + 3 labor) × 40 days" },
    ],
  },
];

const distillationProducts: ProductCard[] = [
  {
    model: "Batch Design MSBD-23001",
    specs: [
      { label: "Design", value: "Batch/Automatic" },
      { label: "Capacity", value: "5~50 tons/day", highlight: true },
      { label: "Heating Process", value: "Without catalyst" },
      { label: "End Product Color", value: "Dark / Looks like diesel" },
      { label: "End Product Smell", value: "Nothing / Meet standard" },
      { label: "Quality Standard", value: "—" },
      { label: "Appearance After Storage", value: "Color and smell will change" },
      { label: "Usage", value: "Heating as furnace oil" },
    ],
  },
  {
    model: "Semi-Continuous MSSD-24002",
    specs: [
      { label: "Design", value: "Semi-Automatic" },
      { label: "Capacity", value: "5~350 tons/day", highlight: true },
      { label: "Heating Process", value: "+ Acid/Alkali Reaction" },
      { label: "End Product Color", value: "Looks like diesel" },
      { label: "End Product Smell", value: "Nothing / Meet standard" },
      { label: "Quality Standard", value: "Some index meet standard" },
      { label: "Appearance After Storage", value: "Color and smell will change" },
      { label: "Usage", value: "Boat, tractors (crude diesel)" },
    ],
  },
  {
    model: "Top Configuration MSCD-24008",
    specs: [
      { label: "Design", value: "Fully-Automatic" },
      { label: "Capacity", value: "5~350 tons/day", highlight: true },
      { label: "Heating Process", value: "Pretreating + Gasified Catalyst + Filter System" },
      { label: "End Product Color", value: "Same as diesel" },
      { label: "End Product Smell", value: "Same as diesel" },
      { label: "Quality Standard", value: "All meet standard (only sulfur content difficult to meet Euro/National standard)" },
      { label: "Appearance After Storage", value: "Fixed color and smell can be kept for 6 months" },
      { label: "Usage", value: "Power oil for engines and electric generator" },
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

function ProductCardComponent({ card, index }: { card: ProductCard; index: number }) {
  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
    >
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
        <h3 className="text-lg font-bold text-slate-900">{card.model}</h3>
      </div>
      <div className="px-6 py-5 space-y-3">
        {card.specs.map((spec) => (
          <div key={spec.label} className="grid grid-cols-[180px_1fr] gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr]">
            <span className="text-slate-500 text-sm font-semibold leading-snug">{spec.label}</span>
            <span
              className={`text-sm leading-snug ${
                spec.highlight ? "text-primary font-bold text-base" : "text-slate-900"
              }`}
            >
              {spec.value}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Products() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      {/* Hero Banner */}
      <section className="bg-slate-900 py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Our Products
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto"
          >
            Industrial-grade thermal conversion equipment — from batch processing to fully continuous
            operation.
          </motion.p>
        </div>
      </section>

      {/* Pyrolysis Plants */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 p-2.5 rounded-xl">
                <Flame className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">Pyrolysis Plants</h2>
            </div>
            <p className="text-slate-600 text-lg max-w-4xl leading-relaxed">
              Convert waste tyres and plastics into fuel oil (45%), carbon black (38%), and steel wire
              (12%). Four configurations available — from batch processing to fully continuous
              operation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {pyrolysisProducts.map((card, i) => (
              <ProductCardComponent key={card.model} card={card} index={i} />
            ))}
          </div>

          <p className="mt-8 text-sm text-slate-500 italic leading-relaxed">
            *Customize machine structure according to different feedstock characteristics: anti-coking
            structure for waste oil sludge, de-waxer for waste plastic, high-efficiency heating &
            cooling structure for waste rubber tyre.
          </p>
        </div>
      </section>

      {/* Distillation Plants */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 p-2.5 rounded-xl">
                <Droplets className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">Distillation Plants</h2>
            </div>
            <p className="text-slate-600 text-lg max-w-4xl leading-relaxed">
              Refine pyrolysis oil and waste engine oil into diesel-grade fuel. Three configurations
              from batch to fully automatic continuous processing.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {distillationProducts.map((card, i) => (
              <ProductCardComponent key={card.model} card={card} index={i} />
            ))}
          </div>

          <p className="mt-8 text-sm text-slate-500 italic leading-relaxed">
            *Customize technology according to different feedstock: continuous design for good quality
            feedstock like purity waste engine oil, semi-continuous design for high ash content
            feedstock like pyrolysis fuel oil.
          </p>
        </div>
      </section>
    </div>
  );
}
